import { type ReactNode, createContext, useContext } from 'react'

/**
 * MockAuthProvider.tsx
 * Dev-only: replaces <MsalProvider> when VITE_DISABLE_AUTH=true.
 *
 * Stubs out the three MSAL React hooks used across the app so no real
 * Azure AD login is required during local development:
 *   - useIsAuthenticated() → always true
 *   - useMsal()            → fake instance + a local dev account
 *   - MsalProvider         → transparent passthrough
 *
 * NEVER use this in a deployed environment.
 */

// ── Test/Dev-only mock token values ────────────────────────────────────────────
// These are placeholder values used only in development.
// They are NOT real secrets and must never be used in production.
const TEST_MOCK_TOKEN = 'dev-test-token-not-a-real-secret'
const TEST_MOCK_ACCESS_TOKEN = 'dev-test-access-token-not-a-real-secret'

// ── Mock context values ────────────────────────────────────────────────────────
const MOCK_ACCOUNT = {
  homeAccountId:          'mock-home-account-id',
  environment:            'login.microsoftonline.com',
  tenantId:               '36da45f1-dd2c-4d1f-af13-5abe46b99921',
  username:               'devuser@deloitte.com',
  localAccountId:         'mock-local-account-id',
  name:                   'Dev User',
  idTokenClaims:          { preferred_username: 'devuser@deloitte.com' },
  nativeAccountId:        undefined,
  authorityType:          'MSSTS' as const,
  tenantProfiles:         new Map(),
}

const MOCK_INSTANCE = {
  loginRedirect:      () => Promise.resolve(),
  loginPopup:         () => Promise.resolve({ accessToken: TEST_MOCK_TOKEN, account: MOCK_ACCOUNT } as never),
  logoutRedirect:     () => Promise.resolve(),
  logoutPopup:        () => Promise.resolve(),
  acquireTokenSilent: () => Promise.resolve({ accessToken: TEST_MOCK_ACCESS_TOKEN } as never),
  acquireTokenRedirect: () => Promise.resolve(),
  getActiveAccount:   () => MOCK_ACCOUNT,
  getAllAccounts:      () => [MOCK_ACCOUNT],
  setActiveAccount:   () => undefined,
  handleRedirectPromise: () => Promise.resolve(null),
  initialize:         () => Promise.resolve(),
}

// ── Context ───────────────────────────────────────────────────────────────────
const MockMsalContext = createContext({
  instance: MOCK_INSTANCE,
  accounts: [MOCK_ACCOUNT],
  inProgress: 'none' as const,
})

// ── Module-level mock overrides for @azure/msal-react hooks ──────────────────
// These are read by useIsAuthenticated() / useMsal() when MockAuthProvider is in the tree.
// We shadow the real exports by making them read from our context.

/**
 * useIsAuthenticated — returns true when MockAuthProvider is active.
 * Import from '@/features/auth/MockAuthProvider' in components that need it during dev,
 * OR see providers.tsx which globally patches the hooks when VITE_DISABLE_AUTH=true.
 */
export function useMockIsAuthenticated(): boolean {
  return true
}

export function useMockMsal() {
  return useContext(MockMsalContext)
}

// ── Provider component ────────────────────────────────────────────────────────
interface Props {
  children: ReactNode
}

export function MockAuthProvider({ children }: Props) {
  return (
    <MockMsalContext.Provider value={{ instance: MOCK_INSTANCE, accounts: [MOCK_ACCOUNT], inProgress: 'none' }}>
      {children}
    </MockMsalContext.Provider>
  )
}
